import {
  ArrowDownward,
  ArrowUpward,
  Cached,
  Group,
  Logout,
  PaidOutlined,
  VerifiedUser,
} from "@mui/icons-material";
import { Typography, useTheme } from "@mui/material";
import {
  Chain,
  CHAIN_CHECK_LIST,
  CHAIN_CONFIG,
  ChainCurrency,
  ChainSymbol,
  NATIVE_CURRENCY_CHECK_LIST,
  WRAPPED_CURRENCY_CHECK_LIST,
  WrappedCurrency,
} from "chainConfigs";
import { Value } from "components/Value";
import { CHAIN_EXPLORER, CHAIN_DECIMAL, ROUTES, SOLANA } from "consts";
import { PortfolioProvider, usePortfolioContext } from "contexts/PortfolioContext";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosService from "services/axios";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { Card, Col, Row } from "v2/components/base/Flex/ui";
import Skeleton from "v2/components/base/Skeleton/ui";
import BasicButton from "v2/components/Buttons/BasicButton";
import CoinAvatar from "v2/components/CoinAvatar/ui";
import EmailUpdateModal from "v2/components/Models/EmailUpdateModal";
import { PercentageBlock, PricePerformanceBlock } from "v2/components/NumBlock/ui";
import DetailPageLayout from "v2/layouts/DetailPageLayout";
import { MainPageLayout } from "v2/layouts/MainPageLayout";
import wEthAbi from "lib/erc/wEthAbi.json";
import useToastStore from "store/toast-store/useToastStore";
import { UserPortfolioAssets } from "types";
import { UNSUPPORTED_SWAP_TOKENS } from "tradeConfigs";
import { useTxnSign } from "hooks/useTxnSign";
import { NATIVE_TOKEN_MAP } from "components/CoinAvatar";
import { TradeProvider } from "contexts/TradeContext";
import { CrossChainTradeDrawerV2 } from "v2/components/CrossChainSwapDrawer";
import { CrossChainDepositModal } from "v2/components/CrossChainDeposit/ui";
import { useEmailAuth } from "hooks/useEmailAuth";
import { useQuery } from "@tanstack/react-query";
import { ExportPrivateKeyModal } from "v2/components/ExportPrivateKey/ui";
import { SendTokenDrawer } from "v2/components/SendTokenDrawer/ui";
import { AuthCheckBlocker } from "components/Blockers/Blocks/AuthCheckBlocker";
import { useCheckViewPoint, VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";

const PortfolioPage = () => {
  const theme = useTheme();
  const { tgUserName, logoutTgUserV2, checkIsFromMiniApp } = useUserStoreV2();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  return (
    <AuthCheckBlocker>
      <MainPageLayout
        Title={
          <Row type="bc" fullWidth>
            <Typography variant={isMobile ? "mobile-h3" : "mobile-h2"}>Portfolio</Typography>
            {isMobile && (
              <Row
                type="cc"
                sx={{
                  gap: "4px",
                  border: `1px solid ${theme.palette.text.secondary}`,
                  borderRadius: "8px",
                  padding: "0 4px 0 8px",
                }}>
                <Typography variant="mobile-action-s" onClick={logoutTgUserV2}>
                  Logout
                </Typography>
                <Logout sx={{ cursor: "pointer", padding: "4px", fontSize: "28px" }} />
              </Row>
            )}
          </Row>
        }>
        <PortfolioProvider>
          <DetailPageLayout FixedContent={<FixedContent />} ScrollableContent={<Assets />} />
        </PortfolioProvider>
      </MainPageLayout>
    </AuthCheckBlocker>
  );
};

const FixedContent = () => {
  const theme = useTheme();
  const [isOpenEmailAuthUpdateDrawer, setIsOpenEmailAuthUpdateDrawer] = useState(false);
  const navigate = useNavigate();
  const { tgUserName, logoutTgUserV2, checkIsFromMiniApp } = useUserStoreV2();

  const { showToast } = useToastStore();
  const { exportWallet } = useTxnSign();
  const { credential } = useEmailAuth();

  const [isOpennedExportKeyModal, setIsOpennedExportKeyModal] = useState(false);
  const [isOpennedDepositModal, setIsOpennedDepositModal] = useState(false);
  const [isOpenSendDrawer, setIsOpenSendDrawer] = useState(false);

  const [keys, setKeys] = useState<Record<string, string | null>>({
    ethereum: null,
    solana: null,
    ton: null,
  });
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  const {
    userAllPortfolioAssetsData: fullAssetsData,
    isUserAllPortfolioAssetsLoading: isFullAssetsInfoLoading,
    refetchUserAllPortfolioAssets: refetch,
  } = usePortfolioContext();

  const { data: exportedWallet, isLoading: isExportedWalletLoading } = useQuery({
    queryKey: ["decrypted-bundle"],
    queryFn: async () => {
      return exportWallet();
    },
    enabled: !credential.isLoading,
  });
  const [isLoadingSend, setIsLoadingSend] = useState(false);

  useEffect(() => {
    if (!isFullAssetsInfoLoading && isLoadingSend) {
      setIsOpenSendDrawer(true);
      setIsLoadingSend(false);
    }
  }, [isFullAssetsInfoLoading, isLoadingSend]);

  const { decryptedBundle } = exportedWallet ?? {};

  useEffect(() => {
    if (isOpenSendDrawer && !isFullAssetsInfoLoading) {
      refetch();
    }
  }, [isFullAssetsInfoLoading, isOpenSendDrawer, refetch]);

  useEffect(() => {
    if (exportedWallet && !isExportedWalletLoading) {
      if (!keys.solana && decryptedBundle) {
        setKeys((pre) => ({
          ...pre,
          solana: decryptedBundle,
          ethereum: decryptedBundle,
        }));
      }
    }

    if (isLoadingKeys && !isOpennedExportKeyModal && !isExportedWalletLoading) {
      setIsOpennedExportKeyModal(true);
      setIsLoadingKeys(false);
    }
  }, [
    decryptedBundle,
    exportedWallet,
    isExportedWalletLoading,
    isLoadingKeys,
    isOpennedExportKeyModal,
    keys.solana,
  ]);

  const {
    userAllPortfolioAssetsData: data,
    isUserAllPortfolioAssetsLoading: isLoading,
    isUserAllPortfolioAssetsRefetching: isRefetching,
    refetchFullAllData,
  } = usePortfolioContext();

  const handleOpenDepositeModal = () => {
    setIsOpennedDepositModal(true);
  };

  const handleCloseDepositeModal = () => {
    setIsOpennedDepositModal(false);
  };

  const handleOpenExportKeyModal = async () => {
    setIsLoadingKeys(true);
  };

  const handleCloseExportKeyModal = () => {
    setIsOpennedExportKeyModal(false);
  };

  const handleOpenSendDrawer = () => {
    setIsLoadingSend(true);
  };

  const handleCloseSendDrawer = () => {
    setIsOpenSendDrawer(false);
  };

  const navigateToReferral = () => {
    navigate(ROUTES.referral);
  };

  const handleOpenEmailAuthUpdateDrawer = () => {
    return setIsOpenEmailAuthUpdateDrawer(true);
  };

  const handleCloseEmailAuthUpdateDrawer = () => {
    return setIsOpenEmailAuthUpdateDrawer(false);
  };

  return (
    <>
      <Card.Section sx={{ width: "100%" }}>
        <Col section="wallet-summary" fullWidth>
          <Row type="bs" sx={{ gap: "16px" }} fullWidth>
            <BasicButton variant="white" onClick={navigateToReferral}>
              <Row type="cc" sx={{ gap: "8px" }}>
                <Typography variant="mobile-body-s-heavy">Referral</Typography>
                <Group />
              </Row>
            </BasicButton>
            <BasicButton variant="black" onClick={handleOpenEmailAuthUpdateDrawer}>
              <Row type="cc" sx={{ gap: "8px" }}>
                <VerifiedUser />
              </Row>
            </BasicButton>
          </Row>
          <Col type="cc" sx={{ paddingY: "48px", gap: "16px" }} fullWidth>
            <Row type="bc" sx={{ gap: "8px" }}>
              <Typography variant="mobile-h2">{tgUserName}</Typography>
            </Row>
            <Col type="cc" sx={{ gap: "8px" }} fullWidth>
              <Skeleton.Dot
                isLoading={isLoading}
                sx={{ display: "flex", justifyContent: "center" }}
                skeletonHeight="83px">
                <Value
                  variant="mobile-display-m"
                  color="brand.primary"
                  value={data?.totalAssetsValue}
                  isPrice
                  useFixedDecimals
                  useKMBTFormat={false}
                />
                <Row type="cc" sx={{ gap: "8px" }}>
                  <Card.Badge>
                    <Typography variant="mobile-body-s-heavy">
                      <Value
                        variant="mobile-body-s-heavy"
                        value={data?.totalPerformanceInUsd}
                        useFixedDecimals
                        useKMBTFormat={false}
                      />
                      {` USD`}
                    </Typography>
                  </Card.Badge>
                  <PercentageBlock percentage={data?.totalPerformance} />
                  <Cached
                    onClick={() => refetchFullAllData()}
                    sx={{
                      cursor: "pointer",
                      width: "20px",
                      height: "20px",
                      ...((isLoading || isRefetching) && {
                        animation: "spin 2s linear infinite",
                        "@keyframes spin": {
                          "0%": {
                            transform: "rotate(360deg)",
                          },
                          "100%": {
                            transform: "rotate(0deg)",
                          },
                        },
                      }),
                    }}
                  />
                </Row>
              </Skeleton.Dot>
            </Col>
          </Col>
          <Row type="bs" sx={{ gap: "12px" }} fullWidth>
            <BasicButton variant="black" sx={{ flex: 1 }} onClick={handleOpenDepositeModal}>
              <Row type="cc" sx={{ gap: "8px" }}>
                <Typography variant="mobile-body-s-heavy">Deposit</Typography>
                <ArrowDownward fontSize="small" />
              </Row>
            </BasicButton>
            <BasicButton variant="black" sx={{ flex: 1 }} onClick={handleOpenSendDrawer}>
              <Row type="cc" sx={{ gap: "8px" }}>
                <Typography variant="mobile-body-s-heavy">Send</Typography>
                <ArrowUpward fontSize="small" />
              </Row>
            </BasicButton>
            <BasicButton variant="black" sx={{ flex: 1 }} onClick={handleOpenExportKeyModal}>
              <Row type="cc" sx={{ gap: "8px" }}>
                <Typography variant="mobile-body-s-heavy">Export</Typography>
                <PaidOutlined fontSize="small" />
              </Row>
            </BasicButton>
          </Row>
        </Col>
      </Card.Section>
      {/* Modals */}
      <EmailUpdateModal
        open={isOpenEmailAuthUpdateDrawer}
        onClose={handleCloseEmailAuthUpdateDrawer}
      />
      <CrossChainDepositModal
        defaultChain={SOLANA}
        chains={CHAIN_CHECK_LIST}
        isOpen={isOpennedDepositModal}
        close={handleCloseDepositeModal}
        onCopySuccess={() => {
          showToast({
            variant: "success",
            message: "Copied address to clipboard",
            duration: 3000,
          });
        }}
      />
      <ExportPrivateKeyModal
        defaultChain={SOLANA}
        chains={CHAIN_CHECK_LIST}
        keys={keys}
        isOpen={isOpennedExportKeyModal}
        close={handleCloseExportKeyModal}
        onCopySuccess={() => {
          showToast({
            variant: "success",
            message: "Copied to clipboard",
            duration: 3000,
          });
        }}
      />
      <SendTokenDrawer
        version="v2"
        data={fullAssetsData?.results ?? []}
        isOpen={isOpenSendDrawer}
        close={handleCloseSendDrawer}
      />
    </>
  );
};

const Assets = () => {
  const theme = useTheme();
  const [shouldTradeDrawerOpen, setShouldTradeDrawerOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedChain, setSelectedChain] = useState<Chain>("base");
  const [data, setData] = useState<any>([]);
  const [isFetchingLatestData, setIsFetchingLatestData] = useState(false);
  const { updateUserBalance, isLoading: isUpdatingUserBalance } = useTradeStore();
  const {
    assetsData,
    isAssetsLoading: isLoading,
    refetchAssetsAndTotalAssets: refetch,
    isAssetsRefetching: isRefetching,
    hasAllFetchedAssets: hasAllFetched,
    assetsRef: ref,
  } = usePortfolioContext();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  useEffect(() => {
    if (!isUpdatingUserBalance && !isRefetching && !isLoading) {
      setData({ ...assetsData });
    }
  }, [assetsData, isUpdatingUserBalance, isRefetching, isLoading]);

  const refetchAssets = useCallback(
    async (symbol: string) => {
      const MAX_ATTEMPTS = 10;
      let attempts = 0;
      let isSymbolFound = true;

      while (isSymbolFound && attempts < MAX_ATTEMPTS) {
        setIsFetchingLatestData(true);
        await refetch();
        attempts++;

        const asset = data.pages
          ?.flatMap((page: any) => page.results)
          .find((item: any) => item.symbol === symbol);

        isSymbolFound = !!asset;

        if (isSymbolFound) {
          // Wait a bit before next attempt
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      await updateUserBalance();
      setIsFetchingLatestData(false);
    },
    [refetch, updateUserBalance],
  );

  const onClickShowDrawer = (chain: Chain) => {
    setSelectedChain(chain);
    setShouldTradeDrawerOpen(true);
  };

  useEffect(() => {
    updateUserBalance();
  }, []);
  return (
    <Card.Section sx={{ width: "100%" }}>
      <Col section="assets" sx={{ gap: "20px" }} fullWidth>
        <Skeleton.Logo
          isLoading={isLoading || !data || !data.pages || isRefetching}
          sx={{ aspectRatio: 1 }}>
          <Typography variant="mobile-body-m-heavy">Assets</Typography>
          <Row type="bs" sx={{ gap: "12px" }} fullWidth>
            <Col type="cs" sx={{ flex: 2 }}>
              <Typography variant="mobile-body-s">Token</Typography>
            </Col>
            <Col type="cc" sx={{ flex: 1 }}>
              <Typography variant="mobile-body-s">Price</Typography>
            </Col>
            <Col type="ce" sx={{ flex: 1 }} />
          </Row>
          <Col sx={{ gap: "20px" }} fullWidth fullHeight>
            {data && data.pages
              ? data.pages
                  .flatMap((page: any) => page.results)
                  .map((item: any) => (
                    <AssetsListItem
                      key={`${item.symbol}-${item.chain}-${item.tokenAddress}`}
                      data={item}
                      refetch={refetchAssets}
                      isProcessing={isProcessing}
                      onClickShowDrawer={onClickShowDrawer}
                    />
                  ))
              : null}
            {isMobile ? <Row sx={{ minHeight: "140px" }} fullWidth /> : null}
            {shouldTradeDrawerOpen ? (
              <TradeProvider
                chain={selectedChain}
                mode="SWAP"
                targetTokenConfig={{
                  solana: {
                    chain: CHAIN_CONFIG["solana"].name,
                    symbol: CHAIN_CONFIG["solana"].currency,
                    token: CHAIN_CONFIG["solana"].nativeTokenAddress,
                  },
                  ethereum: {
                    chain: CHAIN_CONFIG["ethereum"].name,
                    symbol: CHAIN_CONFIG["ethereum"].currency,
                    token: CHAIN_CONFIG["ethereum"].nativeTokenAddress,
                  },
                  base: {
                    chain: CHAIN_CONFIG["base"].name,
                    symbol: CHAIN_CONFIG["base"].currency,
                    token: CHAIN_CONFIG["base"].nativeTokenAddress,
                  },
                  berachain: {
                    chain: CHAIN_CONFIG["berachain"].name,
                    symbol: CHAIN_CONFIG["berachain"].currency,
                    token: CHAIN_CONFIG["berachain"].nativeTokenAddress,
                  },
                  bsc: {
                    chain: CHAIN_CONFIG["bsc"].name,
                    symbol: CHAIN_CONFIG["bsc"].currency,
                    token: CHAIN_CONFIG["bsc"].nativeTokenAddress,
                  },
                }}>
                <CrossChainTradeDrawerV2
                  mode="SWAP"
                  open={shouldTradeDrawerOpen}
                  groupId={0}
                  chain={selectedChain}
                  onConfirm={() => {
                    setShouldTradeDrawerOpen(false);
                    updateUserBalance();
                  }}
                  onFail={() => {
                    setIsProcessing(false);
                    setShouldTradeDrawerOpen(false);
                  }}
                  onProcessing={() => {
                    setIsProcessing(true);
                  }}
                  onProcessed={async () => {
                    setIsProcessing(false);
                    await Promise.all([refetch(), updateUserBalance()]);
                  }}
                  onClose={async () => {
                    setShouldTradeDrawerOpen(false);
                    await Promise.all([refetch(), updateUserBalance()]);
                  }}
                />
              </TradeProvider>
            ) : null}
          </Col>
        </Skeleton.Logo>
      </Col>
    </Card.Section>
  );
};

const AssetsListItem = ({
  data,
  refetch,
  isProcessing,
  onClickShowDrawer,
}: {
  data: UserPortfolioAssets;
  refetch: (symbol: string) => Promise<any>;
  isProcessing: boolean;
  onClickShowDrawer: (chain: Chain) => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToastStore();
  const { walletAddresses, isCustodial } = useUserStoreV2();
  const { signAndWriteContract, signAndBroadcastSolanaTxn } = useTxnSign();

  const [isLoading, setIsLoading] = useState(false);

  const symbol = data.symbol.toUpperCase();
  const isNativeToken = NATIVE_CURRENCY_CHECK_LIST.includes(symbol as ChainCurrency);
  const isWrappedToken = WRAPPED_CURRENCY_CHECK_LIST.includes(symbol as WrappedCurrency);
  const isUnsupportedToken = UNSUPPORTED_SWAP_TOKENS.includes(symbol as ChainSymbol);

  const buttonText = isNativeToken ? "Swap" : isWrappedToken ? "Unwrap" : "Trade";

  const navigateToTrade = async () => {
    if (isNativeToken) {
      onClickShowDrawer(data.chain);
      return;
    }

    if (isWrappedToken) {
      try {
        setIsLoading(true);
        showToast({
          variant: "info",
          message: "Processing now. Please wait.",
          // duration: 5000,
        });

        let res;

        if (["WETH", "WBNB", "WBERA"].includes(symbol)) {
          if (isCustodial) {
            res = await axiosService.unwrapEth({
              amount: Number(data.balance),
              senderAddress: walletAddresses?.ethAddress!,
              tokenAddress: data.tokenAddress,
              chain: data.chain,
            });
          } else {
            const calldata = {
              abi: wEthAbi,
              address: data.tokenAddress,
              functionName: "withdraw",
              args: [Number((Number(data.balance) * CHAIN_DECIMAL.ethereum).toFixed(10))],
            };

            const hash = await signAndWriteContract(
              data.chain,
              walletAddresses?.ethAddress,
              calldata,
            );

            if (hash) {
              res = {
                status: 200,
                message: `Transaction executed successfully: <a target="_blank" href='${
                  CHAIN_EXPLORER[data.chain]
                }/${hash}'>Link</a>`,
              };
            }
          }
        } else {
          const unWrapRes = await axiosService.unwrapSol({
            senderAddress: walletAddresses?.solAddress!!,
            tokenAddress: data.tokenAddress,
          });

          if (unWrapRes.status < 300) {
            const signedTxn = await signAndBroadcastSolanaTxn(
              walletAddresses?.solAddress!,
              unWrapRes.versionedTransaction,
            );

            res = await axiosService.sendToken({
              receiverAddress: walletAddresses?.solAddress!,
              amount: Number(data.balance),
              chain: data.chain,
              signedTxn,
            });
          }
        }

        if (res && res.status < 300) {
          showToast({
            variant: "success",
            message: res.message,
          });
        } else {
          showToast({
            variant: "error",
            message: res?.message,
          });
        }
      } catch (error) {
        console.error("Failed to unwrap token", error);
        showToast({
          variant: "error",
          message: "Failed to unwrap token",
        });
      } finally {
        await refetch(data.symbol);
        setIsLoading(false);
      }

      return;
    }

    navigate({
      pathname: `/token/${data.tokenAddress}`,
      search: `chain=${data.chain}${
        data.tgGroupId ? `&groupId=${data.tgGroupId}` : ""
      }&from=portfolio`,
    });
  };

  return (
    <Row type="bc" sx={{ gap: "12px" }} fullWidth>
      <Row type="sc" sx={{ flex: 2, gap: "8px" }}>
        <Card.Item
          sx={{
            backgroundColor: `${theme.palette.surface.bg}`,
            padding: "6px",
            borderRadius: "24px",
          }}>
          <Row type="cc" sx={{ gap: "8px" }}>
            <CoinAvatar
              chain={data.chain}
              iconUri={data.imageUrl ? data.imageUrl : NATIVE_TOKEN_MAP[symbol]}
            />
            <Typography variant="mobile-body-s-heavy">{data.symbol.toUpperCase()}</Typography>
          </Row>
        </Card.Item>
      </Row>
      <Col type="cc" sx={{ flex: 1, gap: "4px" }}>
        <Value value={data.priceUsd} isPrice />
        <PricePerformanceBlock performance={data.performance ? +data.performance.toFixed(4) : 0} />
      </Col>
      <Row type="ec" sx={{ flex: 1, gap: "8px" }}>
        <BasicButton
          variant="white"
          onClick={navigateToTrade}
          disabled={isUnsupportedToken || isLoading || isProcessing}>
          <Typography
            variant="mobile-body-s-heavy"
            sx={{ textTransform: "capitalize" }}
            color={
              isUnsupportedToken || isLoading || isProcessing ? "text.secondary" : "text.btn-dark"
            }>
            {buttonText.toLowerCase()}
          </Typography>
        </BasicButton>
      </Row>
    </Row>
  );
};

export default PortfolioPage;
